import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

import { InstallerSchedulePage } from "@/pages/InstallerSchedule";
import { RegistryOutsourcesPage } from "@/pages/RegistryOutsources";
import { InstallerScheduleHoursTable } from "@/widgets/installers/InstallerScheduleHoursTable";
import { InstallerScheduleTable } from "@/widgets/installers/InstallerScheduleTable";
import { getRouteInstallerScheduleTable } from "@/shared/const/router";
import { InstallerSummaryReport } from "@/widgets/installers/InstallerSummaryReport";
import { InstallerEquipmentReport } from "@/widgets/installers/InstallerEquipmentReport";

import {
	getStatusAccessForRO,
	getStatusAccessForInstallerSchedule,
	getStatusAccessForInstallerEquipmentReport,
} from "@/helpers/userAccess";

const LoginAsync = () => import("@/views/Login.vue");
const MobileAsync = () => import("@/views/Mobile.vue");
const CatalogAsync = () => import("@/components/catalog/catalog.vue");
const CatalogListAsync = () => import("@/components/catalog/catalogList.vue");
const FormListAsync = () => import("@/components/form/formList.vue");
const FormViewAsync = () => import("@/components/form/form.vue");
const FormConstructorAsync = () => import("@/components/form/formConstructor.vue");
const TaskAsync = () => import("@/components/task/task.vue");
const ProfileAsync = () => import("@/components/user/profile.vue");
const ProfilesAsync = () => import("@/components/user/profiles.vue");
const DepartmentAsync = () => import("@/components/department/department.vue");
const DepartmentsListAsync = () => import("@/components/department/departmentList.vue");
const BotProfileAsync = () => import("@/components/bot/botProfile.vue");
const BotsListAsync = () => import("@/components/bot/botsList.vue");
const CompanyAsync = () => import("@/components/company/company.vue");
const RegistryAsync = () => import("@/components/registry/registry.vue");
const DeepSearchListAsync = () => import("@/components/deepSearch/deepSearchList.vue");
const TaskList = () => import("@/components/task/taskList.vue");
const PpaAsync = () => import("@/components/ppa/ppa.vue");
const resetPasswordAsync = () => import("@/components/resetPassword/resetPassword.vue");

const routes: Array<RouteRecordRaw> = [
	{
		path: "/",
		name: "Home",
		component: TaskList,
	},
	{
		path: "/login",
		name: "Login",
		component: LoginAsync,
	},
	{
		path: "/mobile",
		name: "Mobile",
		component: MobileAsync,
	},
	{
		path: "/catalog/:id",
		name: "Catalog",
		component: CatalogAsync,
		props: true,
	},
	{
		path: "/catalog/",
		name: "CatalogList",
		component: CatalogListAsync,
	},
	{
		path: "/form/",
		name: "FormList",
		component: FormListAsync,
	},
	{
		path: "/form/:id",
		name: "Form",
		component: FormViewAsync,
		props: (route) => ({ id: Number(route.params.id) }),
	},
	{
		path: "/form/constructor/:id",
		name: "FormConstructor",
		component: FormConstructorAsync,
		props: (route) => ({ id: Number(route.params.id) }),
	},
	{
		path: "/task/",
		name: "TaskList",
		component: TaskList,
		props: () => ({ type: "#all" }),
	},
	{
		path: "/task-incoming/",
		name: "TaskListIncoming",
		component: TaskList,
		props: () => ({ type: "#inbox" }),
	},
	{
		path: "/task-search",
		name: "DeepSearchTaskList",
		component: DeepSearchListAsync,
		props: (route) => ({ id: Number(route.params.id) }),
	},
	{
		path: "/task/:id",
		name: "Task",
		component: TaskAsync,
		props: (route) => ({ id: Number(route.params.id) }),
	},
	{
		path: "/task/:id",
		name: "TaskIncoming",
		component: TaskAsync,
		props: (route) => ({ id: Number(route.params.id) }),
	},
	{
		path: "/task-incoming/:id",
		redirect: (to) => {
			return {
				path: `/task/${to.params.id}`,
			};
		},
	},
	{
		path: "/profile/:id",
		name: "Profile",
		component: ProfileAsync,
		props: (route) => ({ id: Number(route.params.id) }),
	},
	{
		path: "/profiles/",
		name: "Profiles",
		component: ProfilesAsync,
	},
	{
		path: "/departments/",
		name: "Departments",
		component: DepartmentsListAsync,
	},
	{
		path: "/departments/:id",
		name: "Department",
		component: DepartmentAsync,
		props: (route) => ({ id: Number(route.params.id) }),
	},
	{
		path: "/bots/",
		name: "BotsList",
		component: BotsListAsync,
	},
	{
		path: "/bots/:id",
		name: "Bot",
		component: BotProfileAsync,
		props: (route) => ({ id: Number(route.params.id) }),
	},
	{
		path: "/company/",
		name: "Company",
		component: CompanyAsync,
	},
	{
		path: "/registry-outsources/",
		name: "RegistryOutsources",
		component: RegistryOutsourcesPage,
		beforeEnter: () => {
			return getStatusAccessForRO() || { path: "/" };
		},
	},
	{
		path: getRouteInstallerScheduleTable(),
		component: InstallerSchedulePage,
		beforeEnter: () => {
			return getStatusAccessForInstallerSchedule() || { path: "/" };
		},
		children: [
			{
				path: "",
				name: "InstallerSchedule",
				component: InstallerScheduleTable,
			},
			{
				path: "hours",
				name: "InstallerScheduleHours",
				component: InstallerScheduleHoursTable,
			},
			{
				path: "report",
				name: "InstallerSummaryReport",
				component: InstallerSummaryReport,
			},
			{
				path: "equipment",
				name: "InstallerEquipmentReport",
				component: InstallerEquipmentReport,
				beforeEnter: () => {
					return getStatusAccessForInstallerEquipmentReport() || { path: getRouteInstallerScheduleTable() };
				},
			},
		],
	},
	{
		path: "/form/:id/registry/",
		name: "Registry",
		component: RegistryAsync,
	},
	{
  		path: '/ppa/',
  		name: 'RpaPage',
  		component: PpaAsync,
	},
	{
  		path: '/reset-password/',
  		name: 'resetPassword',
  		component: resetPasswordAsync,
	},
];

const router = createRouter({
	history: createWebHistory(import.meta.env.VITE_PUBLIC_PATH),
	routes,
});

export default router;
